import { useEffect } from 'react';
import { Controller, useFormState } from 'react-hook-form';
import { Prompt } from 'react-router';
import { FormError } from 'shared/components/FormControl';
import styled from 'styled-components';

import {
  ReportTemplateFormRow,
  ReportTemplateFormRowField,
  ReportTemplateFormRowLabelWrapper,
} from 'dashboard/forms/FormReportTemplate/ReportTemplateFieldRow';
import ReportTemplateHeader from 'dashboard/forms/FormReportTemplate/ReportTemplateHeader';
import Section from 'dashboard/layouts/FormLayout/Section';
import { ColumnContainer, SoloColumn } from 'dashboard/layouts/ShowViewLayoutV2';
import Card from 'design-system/components/Card';
import Input from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';
import { FontWeight } from 'design-system/variables/text';
import useExternalIntegration from 'lib/hooks/useExternalIntegration';
import { ApiModel } from 'lib/interfaces/api';
import { useFormSecurely } from 'lib/security/reactHookForm';

const StyledLabel = styled(Label)`
  font-weight: ${FontWeight.Bold};
`;

interface FormReportTemplateSentilinkScoresProps {
  canEdit: boolean;
  canEditConfig: boolean;
  submit: (formValues: ReportTemplateSentilinkScoresConfigFormData) => void;
  defaultValues: ReportTemplateSentilinkScoresConfigFormData;
  isLoadingPage: boolean;
  latestPublishedVersionId: string;
  helpCenterUrl?: string;
}

export const FormReportTemplateSentilinkScores = ({
  canEdit,
  canEditConfig,
  submit,
  defaultValues,
  isLoadingPage,
  latestPublishedVersionId,
  helpCenterUrl,
}: FormReportTemplateSentilinkScoresProps) => {
  const form = useFormSecurely<ReportTemplateSentilinkScoresConfigFormData>({
    mode: 'onChange',
    defaultValues,
  });
  const { register, handleSubmit, control, getValues, reset } = form;
  const { isSubmitting, isDirty, errors, isValid, isSubmitted } = useFormState({
    control,
  });

  const [sentilinkCredentials] = useExternalIntegration([
    ApiModel.ExternalIntegrationEnrichmentSentilink,
  ]);
  const sentilinkCredentialOptions = [
    ...sentilinkCredentials.map((integration) => ({
      label: integration.attributes.name,
      value: integration.id,
    })),
  ];

  const disabled = !canEdit || isLoadingPage || isSubmitting;
  const disabledConfig = disabled || !canEditConfig;

  useEffect(() => {
    if (isSubmitted) {
      reset(getValues());
    }
  }, [isSubmitted, getValues, reset]);

  return (
    <>
      <Prompt
        when={isDirty}
        message='You have unsaved changes. Are you sure you want to leave this page?'
      />
      <form onSubmit={handleSubmit(submit)}>
        <ReportTemplateHeader
          saveDisabled={disabled || !isDirty || !isValid}
          reportTemplateVersionId={latestPublishedVersionId}
        />
        <ColumnContainer>
          <SoloColumn>
            <Card>
              <Card.Body>
                <Section cols={1}>
                  <StyledLabel
                    size='lg'
                    color={disabled ? 'disabled' : 'primary'}
                    htmlFor='form-report-template-sentilink-scores__name'
                  >
                    Template name
                  </StyledLabel>
                  <Spacer height={2} />
                  <Input
                    {...register('name', {
                      required: 'A template name is required',
                    })}
                    id='form-report-template-sentilink-scores__name'
                    type='text'
                    placeholder='Name'
                    disabled={disabled}
                  />
                  {errors.name && <FormError>{errors.name.message}</FormError>}
                </Section>
              </Card.Body>
            </Card>
            <Card>
              <Card.Body>
                <ReportTemplateFormRow noBorder={true}>
                  <ReportTemplateFormRowLabelWrapper>
                    <StyledLabel size='lg'>SentiLink Credential</StyledLabel>
                    <Paragraph>The credential to use with this report template.</Paragraph>
                  </ReportTemplateFormRowLabelWrapper>
                  <ReportTemplateFormRowField width='100%'>
                    <Controller
                      control={control}
                      name='external-integration-id'
                      render={({ field: { onChange, value } }) => (
                        <Select
                          onValueChange={onChange}
                          value={value}
                          disabled={disabledConfig}
                          placeholder='Select SentiLink Credential'
                        >
                          {sentilinkCredentialOptions.map((opt) => (
                            <Option key={opt.label} value={opt.value}>
                              {opt.label}
                            </Option>
                          ))}
                        </Select>
                      )}
                    />
                  </ReportTemplateFormRowField>
                </ReportTemplateFormRow>
              </Card.Body>
            </Card>
            {helpCenterUrl && (
              <Card>
                <Card.Body>
                  <StyledLabel size='lg'>Additional resources</StyledLabel>
                  <span>
                    Learn more about configuring this report on the{' '}
                    <a href={helpCenterUrl} target='_blank' rel='noreferrer'>
                      help center
                    </a>
                    .
                  </span>
                </Card.Body>
              </Card>
            )}
          </SoloColumn>
        </ColumnContainer>
      </form>
    </>
  );
};

export interface ReportTemplateSentilinkScoresConfigFormData {
  name: string;
  'external-integration-id'?: string;
}
